/**
 * Current-Week drilldown modal.
 *
 * Cloned from ``ForecastDrilldownModal`` but pivoted on the current-week
 * window. Each tile / chart click on the Current Week sub-tab opens this
 * with a different ``mode`` — the mode picks the schedules-list filter
 * set so the drilldown count lines up with the tile's count.
 *
 * Param building lives in ``currentWeekDrilldownParams.ts``.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Loader2, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown, Download,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';
import { purchasingAPI } from '../../../lib/api';
import type { POLineSchedule, GoodsReceiptNote } from '../../../lib/api';
import apiClient from '../../../lib/api/client';
import { useFormatters } from '../../../contexts/PreferencesContext';
import {
  buildCurrentWeekParams,
  MODE_TITLES,
  getEntityForMode,
  type CurrentWeekDrilldownMode,
  type CurrentWeekDrilldownFilters,
  type DrilldownParamShape,
  type DrilldownEntity,
} from './currentWeekDrilldownParams';

export type { CurrentWeekDrilldownMode, CurrentWeekDrilldownFilters };

/** Coloured pill for the on-time status column. ``null`` renders an
 *  em-dash so empty rows don't shout. ``whitespace-nowrap`` keeps the
 *  pill on a single line even when the cell is narrow. */
function OnTimeStatusPill({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  const map: Record<string, { label: string; cls: string }> = {
    early:   { label: 'Early',   cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    on_time: { label: 'On time', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    late:    { label: 'Late',    cls: 'bg-red-50 text-red-700 border-red-200' },
  };
  const m = map[value] ?? { label: value, cls: 'bg-slate-50 text-slate-600 border-slate-200' };
  return (
    <Badge variant="outline" className={`${m.cls} whitespace-nowrap`}>{m.label}</Badge>
  );
}

/** Coloured pill for the per-schedule OTIF verdict column. */
function OtifStatusPill({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  const map: Record<string, { label: string; cls: string }> = {
    passed:  { label: 'Passed',  cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    pending: { label: 'Pending', cls: 'bg-slate-50 text-slate-600 border-slate-200' },
    failed:  { label: 'Failed',  cls: 'bg-red-100 text-red-900 border-red-300' },
  };
  const m = map[value] ?? { label: value, cls: 'bg-slate-50 text-slate-600 border-slate-200' };
  return (
    <Badge variant="outline" className={`${m.cls} whitespace-nowrap`}>{m.label}</Badge>
  );
}

/** Mute zero numeric values so the eye lands on the non-zero rows. */
function NumCell({ value }: { value: number | string | undefined | null }) {
  const n = typeof value === 'string' ? parseFloat(value) : (value ?? 0);
  if (!n) return <span className="text-muted-foreground">0</span>;
  return <span className="tabular-nums">{n.toLocaleString(undefined, { maximumFractionDigits: 3 })}</span>;
}

interface CurrentWeekDrilldownModalProps {
  open: boolean;
  onClose: () => void;
  mode: CurrentWeekDrilldownMode;
  filters: CurrentWeekDrilldownFilters;
  /** Sun → today (cost / quality bucket window). */
  weekStartIso: string;
  weekEndIso: string;
  /** Sun → Thu/today (delivery bucket — frozen rule). */
  otifWindowStartIso: string;
  otifWindowEndIso: string;
  /** Today (used for arrears: schedule_date < today). */
  todayIso: string;
  /** Optional ISO date for "by day" stacked-bar drilldowns. */
  dayIso?: string;
}

const PAGE_SIZE = 50;

/** Sort-field map for the SCHEDULE-keyed list (``/purchasing/schedules/``). */
const SCHEDULE_SORT_FIELDS: Record<string, string | null> = {
  schedule_date: 'schedule_date',
  po_number: 'po_line__po__po_number',
  line_number: 'po_line__line_number',
  schedule_line_number: 'schedule_line_number_int',
  part_number: 'po_line__part_number',
  description: null,
  supplier_name: 'po_line__po__supplier__name',
  schedule_qty: 'schedule_qty',
  shipped_qty: 'shipped_qty',
  balance_qty: 'balance_qty',
  otif_status: null,
};

interface ColumnDef {
  id: string;
  label: string;
  align: 'left' | 'right';
  /** Tooltip surfaced on the header — explains semantics so the user
   *  doesn't have to guess what each qty column means. */
  hint?: string;
}

const SCHEDULE_COLUMNS: ColumnDef[] = [
  { id: 'schedule_date',        label: 'Schedule Date',  align: 'left' },
  { id: 'po_number',            label: 'PO #',           align: 'left' },
  { id: 'line_number',          label: 'Line',           align: 'right' },
  { id: 'schedule_line_number', label: 'Sched #',        align: 'right', hint: 'Schedule-line index within the PO line' },
  { id: 'part_number',          label: 'Part',           align: 'left' },
  { id: 'description',          label: 'Description',    align: 'left' },
  { id: 'supplier_name',        label: 'Supplier',       align: 'left' },
  { id: 'schedule_qty',         label: 'Sched Qty',      align: 'right', hint: 'Quantity scheduled for delivery on this date' },
  { id: 'shipped_qty',          label: 'Shipped Qty',    align: 'right', hint: 'Quantity received against this schedule (sum of GRN allocations)' },
  { id: 'balance_qty',          label: 'Open Qty',       align: 'right', hint: 'Outstanding qty = Sched Qty − Shipped Qty' },
  { id: 'otif_status',          label: 'OTIF Status',    align: 'left',  hint: 'Per-schedule OTIF verdict: Passed / Late in-full / On-time short / Failed / Pending (not yet past-due)' },
];

/** Sort-field map for the GRN-keyed list (``/purchasing/grns/``). */
const GRN_SORT_FIELDS: Record<string, string | null> = {
  receipt_date: 'receipt_date',
  po_number: 'po_line__po__po_number',
  line_number: 'po_line__line_number',
  schedule_line_number: null,
  schedule_date: 'po_line_schedule__schedule_date',
  part_number: 'po_line__part_number',
  description: null,
  supplier_name: 'po_line__po__supplier__name',
  schedule_qty: 'po_line_schedule__schedule_qty',
  quantity_received: 'quantity_received',
  on_time_status: 'on_time_status',
};

const GRN_COLUMNS: ColumnDef[] = [
  { id: 'receipt_date',         label: 'Receipt Date',   align: 'left' },
  { id: 'po_number',            label: 'PO #',           align: 'left' },
  { id: 'line_number',          label: 'Line',           align: 'right' },
  { id: 'schedule_line_number', label: 'Sched #',        align: 'right', hint: 'Schedule line this GRN was attributed to' },
  { id: 'schedule_date',        label: 'Sched Date',     align: 'left',  hint: 'Date this schedule slot was due — receipt date vs. this drives the on-time verdict' },
  { id: 'part_number',          label: 'Part',           align: 'left' },
  { id: 'description',          label: 'Description',    align: 'left' },
  { id: 'supplier_name',        label: 'Supplier',       align: 'left' },
  { id: 'schedule_qty',         label: 'Sched Qty',      align: 'right', hint: 'Quantity scheduled for delivery on this date (per schedule slot)' },
  { id: 'quantity_received',    label: 'Received Qty',   align: 'right', hint: 'Quantity received on this GRN' },
  { id: 'on_time_status',       label: 'On-time Status', align: 'left',  hint: 'Receipt vs schedule date verdict (Early / On time / Late)' },
];

function buildPageList(current: number, total: number): Array<number | '…'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: Array<number | '…'> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push('…');
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push('…');
  out.push(total);
  return out;
}

type AnyRow = POLineSchedule | GoodsReceiptNote;

export function CurrentWeekDrilldownModal({
  open, onClose, mode, filters,
  weekStartIso, weekEndIso, otifWindowStartIso, otifWindowEndIso,
  todayIso, dayIso,
}: CurrentWeekDrilldownModalProps) {
  const { formatDate } = useFormatters();
  const entity: DrilldownEntity = useMemo(() => getEntityForMode(mode), [mode]);
  const [rows, setRows] = useState<AnyRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState<string>('1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [sortCol, setSortCol] = useState<string>(entity === 'grn' ? 'receipt_date' : 'schedule_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const sortFields = entity === 'grn' ? GRN_SORT_FIELDS : SCHEDULE_SORT_FIELDS;
  const columns = entity === 'grn' ? GRN_COLUMNS : SCHEDULE_COLUMNS;
  const defaultSort = entity === 'grn' ? 'receipt_date' : 'schedule_date';

  const orderingParam = useMemo(() => {
    const field = sortFields[sortCol];
    if (!field) return defaultSort;
    return sortDir === 'desc' ? `-${field}` : field;
  }, [sortCol, sortDir, sortFields, defaultSort]);

  const baseParams = useMemo(
    () => buildCurrentWeekParams({
      mode, filters,
      weekStartIso, weekEndIso,
      otifStartIso: otifWindowStartIso,
      otifEndIso: otifWindowEndIso,
      todayIso, dayIso,
    }),
    [
      mode, filters, weekStartIso, weekEndIso,
      otifWindowStartIso, otifWindowEndIso, todayIso, dayIso,
    ],
  );

  const fetchPage = useCallback(async (targetPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const params: DrilldownParamShape = {
        ...baseParams,
        page: targetPage,
        page_size: PAGE_SIZE,
        ordering: orderingParam,
      };
      const res = entity === 'grn'
        ? await purchasingAPI.getGRNs(params)
        : await purchasingAPI.getSchedules(params);
      setRows((res.data.results ?? []) as AnyRow[]);
      setTotal(res.data.count ?? 0);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load drilldown';
      setError(msg);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [baseParams, orderingParam, entity]);

  useEffect(() => {
    if (!open) return;
    setPage(1);
    setPageInput('1');
    fetchPage(1);
  }, [open, mode, fetchPage]);

  useEffect(() => {
    if (!open || page === 1) return;
    fetchPage(page);
  }, [page, open, fetchPage]);

  useEffect(() => { setPageInput(String(page)); }, [page]);

  const goToPage = useCallback((p: number) => {
    if (p < 1 || p > totalPages || p === page || loading) return;
    setPage(p);
  }, [page, totalPages, loading]);

  const handleHeaderClick = useCallback((colId: string) => {
    if (!sortFields[colId]) return;
    if (sortCol === colId) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(colId);
      setSortDir('asc');
    }
  }, [sortCol, sortFields]);

  /** Human-readable filename slug from the mode + day so the download
   *  label tells the user what they're opening (no internal mode keys
   *  leaking out of the UI). */
  const exportSlug = useMemo(() => {
    const map: Record<CurrentWeekDrilldownMode, string> = {
      due_this_week:      'schedules_due_this_week',
      open_this_week:     'open_schedules_this_week',
      received_this_week: 'received_schedules_this_week',
      arrears:            'arrears',
      otif_this_week:     'otif_window',
      otd_this_week:      'otd_window',
      day_on_time:        'on_time_deliveries',
      day_early:          'early_deliveries',
      day_late:           'late_deliveries',
      day_pending:        'pending_schedules',
      mix_on_time:        'on_time_deliveries_wtd',
      mix_early:          'early_deliveries_wtd',
      mix_late:           'late_deliveries_wtd',
      mix_otif_pass:    'otif_passed_wtd',
      mix_otif_pending: 'otif_pending_wtd',
      mix_otif_fail:    'otif_failed_wtd',
    };
    const base = map[mode] ?? mode;
    if (dayIso) return `${base}_${dayIso}`;
    const stamp = new Date().toISOString().slice(0, 10);
    return `${base}_${stamp}`;
  }, [mode, dayIso]);

  const handleExport = useCallback(async () => {
    // Server-side stream: same params as the list call, no pagination.
    setExporting(true);
    try {
      const exportParams: DrilldownParamShape = { ...baseParams, ordering: orderingParam };
      const qs = new URLSearchParams(
        Object.entries(exportParams)
          .filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => [k, String(v)]),
      ).toString();
      const path = entity === 'grn'
        ? `/purchasing/grns/export/?${qs}`
        : `/purchasing/schedules/export-xlsx/?${qs}`;
      const resp = await apiClient.get(path, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exportSlug}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel export ready');
    } catch (err: unknown) {
      console.error('Drilldown Excel export failed:', err);
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  }, [baseParams, orderingParam, entity, exportSlug]);

  const sortIcon = (colId: string) => {
    if (!sortFields[colId]) return null;
    if (sortCol !== colId) return <ArrowUpDown className="h-3 w-3 text-muted-foreground inline ml-1" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 text-primary inline ml-1" />
      : <ArrowDown className="h-3 w-3 text-primary inline ml-1" />;
  };

  const pageButtons = useMemo(() => buildPageList(page, totalPages), [page, totalPages]);

  /** Day-of-week hint for "Tue / Wed / ..." in the breadcrumb so users
   *  recognise which bar they drilled into. */
  const dayLabel = useMemo(() => {
    if (!dayIso) return null;
    const d = new Date(dayIso + 'T00:00:00Z');
    return d.toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' });
  }, [dayIso]);

  const noun = entity === 'grn' ? 'delivery' : 'schedule';
  const nounPlural = entity === 'grn' ? 'deliveries' : 'schedules';
  const emptyText = entity === 'grn'
    ? 'No deliveries match this drilldown.'
    : 'No schedules match this drilldown.';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="!max-w-[min(1480px,95vw)] w-[min(1480px,95vw)]">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle>{MODE_TITLES[mode]}</DialogTitle>
              <DialogDescription>
                {total.toLocaleString()} {total === 1 ? noun : nounPlural}
                {dayIso ? <> · {dayLabel ? `${dayLabel} ` : ''}{formatDate(dayIso)}</> : null}
                {filters.part_number ? <> · part <span className="font-mono">{filters.part_number}</span></> : null}
                {filters.supplier_id ? <> · supplier #{filters.supplier_id}</> : null}
                {filters.erp_order_type === '1' ? <> · spot orders</> : <> · scheduling agreements</>}
              </DialogDescription>
            </div>
            <Button
              variant="outline" size="sm"
              onClick={handleExport}
              disabled={exporting || total === 0}
            >
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Export to Excel
            </Button>
          </div>
        </DialogHeader>

        {error && <div className="text-sm text-red-600 py-3">{error}</div>}

        {loading && rows.length === 0 ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground py-10 text-center">
            {emptyText}
          </div>
        ) : (
          <div className="overflow-auto max-h-[60vh] border rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr>
                  {columns.map((c) => {
                    const sortable = sortFields[c.id] !== null;
                    const align = c.align === 'right' ? 'text-right' : 'text-left';
                    const cursor = sortable ? 'cursor-pointer hover:bg-slate-100' : '';
                    return (
                      <th
                        key={c.id}
                        title={c.hint}
                        className={`px-3 py-2 font-medium text-slate-700 select-none whitespace-nowrap ${align} ${cursor}`}
                        onClick={() => handleHeaderClick(c.id)}
                        aria-sort={
                          sortable && sortCol === c.id
                            ? (sortDir === 'asc' ? 'ascending' : 'descending')
                            : undefined
                        }
                      >
                        {c.label}{sortIcon(c.id)}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entity === 'grn'
                  ? (rows as GoodsReceiptNote[]).map((r) => (
                      <tr key={r.id} className="hover:bg-slate-50">
                        <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.receipt_date)}</td>
                        <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{r.po_number ?? '—'}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <NumCell value={r.line_number} />
                        </td>
                        <td className="px-3 py-2 text-right text-xs text-muted-foreground whitespace-nowrap">
                          {r.schedule_line_number ?? '—'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{r.schedule_date ? formatDate(r.schedule_date) : '—'}</td>
                        <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{r.part_number ?? '—'}</td>
                        <td className="px-3 py-2 text-xs max-w-[280px] truncate" title={r.part_description || ''}>
                          {r.part_description || '—'}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground max-w-[180px] truncate" title={r.supplier_name || ''}>
                          {r.supplier_name || '—'}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <NumCell value={r.schedule_qty} />
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <NumCell value={r.quantity_received} />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <OnTimeStatusPill value={r.on_time_status as string | null} />
                        </td>
                      </tr>
                    ))
                  : (rows as POLineSchedule[]).map((r) => (
                      <tr key={r.id} className="hover:bg-slate-50">
                        <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.schedule_date)}</td>
                        <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{r.po_number}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <NumCell value={r.line_number} />
                        </td>
                        <td className="px-3 py-2 text-right text-xs text-muted-foreground whitespace-nowrap">
                          {r.schedule_line_number ?? '—'}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{r.part_number}</td>
                        <td className="px-3 py-2 text-xs max-w-[280px] truncate" title={r.description || ''}>
                          {r.description || '—'}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground max-w-[180px] truncate" title={r.supplier_name || ''}>
                          {r.supplier_name || '—'}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <NumCell value={r.schedule_qty} />
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <NumCell value={r.shipped_qty} />
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <NumCell value={r.balance_qty} />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <OtifStatusPill value={r.otif_status} />
                        </td>
                      </tr>
                    ))
                }
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-2 pt-2 flex-wrap">
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => goToPage(page - 1)} aria-label="Previous page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {pageButtons.map((p, idx) => (
                p === '…' ? (
                  <span key={`gap-${idx}`} className="px-2 text-xs text-muted-foreground">…</span>
                ) : (
                  <Button
                    key={p} size="sm"
                    variant={p === page ? 'default' : 'outline'}
                    disabled={loading}
                    onClick={() => goToPage(p)}
                    aria-current={p === page ? 'page' : undefined}
                    className="min-w-[2.25rem]"
                  >
                    {p}
                  </Button>
                )
              ))}
              <Button size="sm" variant="outline" disabled={page >= totalPages || loading} onClick={() => goToPage(page + 1)} aria-label="Next page">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <label htmlFor="cw-page-jump">Go to page</label>
              <input
                id="cw-page-jump"
                type="number" min={1} max={totalPages}
                value={pageInput} disabled={loading}
                onChange={(e) => setPageInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const n = parseInt(pageInput, 10);
                    if (!Number.isNaN(n)) goToPage(Math.max(1, Math.min(totalPages, n)));
                  }
                }}
                onBlur={() => {
                  const n = parseInt(pageInput, 10);
                  if (!Number.isNaN(n)) goToPage(Math.max(1, Math.min(totalPages, n)));
                  else setPageInput(String(page));
                }}
                className="h-7 w-16 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
              />
              <span>of {totalPages.toLocaleString()}</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
